/**
 * @author       Matthieu Devlin (matt@zumper.com)
 * @copyright    Copyright (c) 2021, Zumper
 * @description  addTrackingToLocalStorage.ts
 */

import { ONE_DAY, ONE_WEEK } from '@zcode/utils'
import type { History } from 'history'
import { GCLID_LOCAL_STORAGE_KEY, MSCLKID_LOCAL_STORAGE_KEY } from '../constants'
import { logger } from '../logger'
import type { LocalStorage } from '../types/LocalStorage'
import { isValidParamLength } from './isValidParamLength'
import { shouldUseLongLife } from './shouldUseLongLife'

const PAID_TRAFFIC_SOURCES = ['bing_sem', 'facebook_sem', 'google_sem', 'trovit']

export const tokenConfig = [
  // [token, storageKey, defaultAge]
  ['P', '_P', 1],
  ['O', '_O', 1],
  ['V', '_V', 1],
  ['C', '_campaign', 7],
  ['R', '_referrer', 7],
  ['T', '_traffic', 7],
  ['TI', '_TI', 7],
  ['EI', '_EI', 7],
  ['onboard', undefined, undefined],
  ['bsft_uid', '_bsft_uid', 7],
  ['bsft_mid', '_bsft_mid', 7],
  ['utm_medium', '_utm_medium', 7],
  ['utm_source', '_utm_source', 7],
  ['utm_campaign', '_utm_campaign', 7],
  ['utm_content', '_utm_content', 7],
  ['template', '_template', 7],
] as const

export const addTrackingToLocalStorage = (
  localStorage: LocalStorage,
  history: History<unknown>
) => {
  const { location } = history || {}
  const { hash, pathname, search } = location || {}

  let isSelfReferrer = false
  if (!localStorage.server && window.document.referrer) {
    const match = window.document.referrer.match(/^https?:\/\/([^/]+)/i)
    if (match) {
      if (/\b(?:padmapper|zumper|zumperrentals)[.]com$/i.test(match[1])) {
        isSelfReferrer = true
      } else {
        localStorage.setItem('_referrer', match[1], ONE_WEEK)
      }
    }
  }

  if (search) {
    let shouldReplaceLocation = false
    const params = new URLSearchParams(search)
    try {
      for (const [token, storageKey, defaultAge] of tokenConfig) {
        if (token === 'R' && !isSelfReferrer) {
          continue
        }
        if (params.has(token)) {
          const value = params.get(token)
          let age: number | undefined = defaultAge
          if (value) {
            if (PAID_TRAFFIC_SOURCES.includes(value)) {
              localStorage.setItem('paid_traffic_entry', true)
            }
            if (shouldUseLongLife(params, token, value)) {
              age = 60
            }
          }
          if (value === '' || value === null) {
            if (token !== 'onboard') {
              localStorage.removeItem(storageKey)
              localStorage.removeItem(`${storageKey as string}r`)
            }
          } else {
            if (token !== 'onboard' && age != null) {
              const parts = value.split('$')
              let results: number | undefined
              for (const option of parts.slice(1, 3)) {
                if (option[0] === 'd') {
                  age = Number.parseInt(option.substr(1), 10)
                } else if (option[0] === 'r') {
                  results = Number.parseInt(option.substr(1), 10)
                }
              }
              if (
                isValidParamLength(parts[0]) &&
                // as the user navigates between Z & ZR, we pass the tracking params back and forth.
                // this could accidentally keep the params stored "forever", since we'd start a new
                // "keep it for 7 days" each time we move between Z & ZR. To prevent that, we skip
                // storing (and resetting the expiry) if the same value is already in local storage
                ((results && localStorage.getx(`${storageKey}r`) !== results) ||
                  localStorage.getx(storageKey) !== parts[0])
              ) {
                localStorage.setItem(storageKey, parts[0], ONE_DAY * age)
                localStorage.setItem(`${storageKey}r`, results, ONE_DAY * age)
              }
            }
            if (!token.includes('utm')) {
              params.delete(token)
            }
            shouldReplaceLocation = true
          }
        }
      }
    } catch (e) {
      logger.error('addTrackingToLocalStorage POVCT failed', search, e)
    }

    if (localStorage.server) {
      return
    }

    // gclid
    try {
      const gclid = params.get('gclid')
      if (gclid && isValidParamLength(gclid)) {
        // delete legacy gclid
        localStorage.removeItem('_gclid')
        const gclsrc = params.get('gclsrc')
        const hasAW = gclsrc?.includes('aw')
        if (!gclsrc || hasAW) {
          localStorage.setItem(GCLID_LOCAL_STORAGE_KEY, gclid, ONE_DAY * 7)
        } else {
          localStorage.removeItem(GCLID_LOCAL_STORAGE_KEY)
        }
      }
    } catch (e) {
      logger.error('addTrackingToLocalStorage gclid failed', search, e)
    }

    // msclid
    try {
      const msclkid = params.get('msclkid')
      if (msclkid && isValidParamLength(msclkid)) {
        localStorage.setItem(MSCLKID_LOCAL_STORAGE_KEY, msclkid, ONE_DAY * 7)
      }
    } catch (e) {
      logger.error('addTrackingToLocalStorage msclkid failed', search, e)
    }

    // replace the search
    if (!localStorage.server && shouldReplaceLocation && history) {
      history.replace({
        hash,
        pathname,
        search: params.toString(),
      })
    }
  }
}
